/**
 * Bati Vision 360 Authentication Service
 * 
 * Unified authentication service that handles all OAuth2/OIDC operations
 * including login, token management, user profile, and API requests.
 * 
 * @author Bati Vision 360 Team
 * @version 2.0.0
 */

import axios, { type AxiosResponse } from 'axios';
import { useAuthStore } from '../stores';
import { generateCodeVerifier, generateCodeChallenge } from '../utils/pkce';
import configService from './config.service';
import { showApiErrorNotification, showApiWarningNotification } from '../utils/globalNotifications';
import type { 
  User, 
  AuthResponse, 
  TokenResponse,
  LoginCredentials
} from '../types';
import type { AppConfig } from '../types/config';

// Configuration interface
interface AuthConfiguration {
  readonly AUTH_SERVER: string;
  readonly TOKEN_REFRESH_BUFFER: number;
  CLIENT_ID: string | null;
  SCOPES: string | null;
  REDIRECT_URI: string | null;
  readonly STORAGE_KEYS: {
    readonly CODE_VERIFIER: string;
    readonly AUTH_STATE: string;
    readonly LOCALE: string;
  };
}

// Configuration constants - Loaded dynamically from config.json
const CONFIG: AuthConfiguration = {
  get AUTH_SERVER(): string { 
    // Use current origin for auth endpoints (relative to current domain)
    return window.location.origin;
  },
  TOKEN_REFRESH_BUFFER: 5 * 60 * 1000, // 5 minutes before expiry
  CLIENT_ID: null, // Will be loaded from config service
  SCOPES: null, // Will be loaded from config service
  REDIRECT_URI: null, // Will be loaded from config service
  STORAGE_KEYS: {
    CODE_VERIFIER: 'code_verifier',
    AUTH_STATE: 'auth_state',
    LOCALE: 'locale'
  }
};

// API Endpoints - Dynamically generated based on configuration
const getEndpoints = () => ({
  AUTHORIZE: `${CONFIG.AUTH_SERVER}/oauth2/authorize`,
  TOKEN: `${CONFIG.AUTH_SERVER}/oauth2/token`,
  LOGOUT: `${CONFIG.AUTH_SERVER}/logout`,
  USER_PROFILE: '/api/user/active'
});

/**
 * PKCE (Proof Key for Code Exchange) utilities - with browser compatibility fallbacks
 */
class PKCEUtils {
  /**
   * Generate a cryptographically secure code verifier with browser compatibility fallbacks
   * @returns {string} Base64URL-encoded code verifier
   */
  static generateCodeVerifier(): string {
    try {
      return generateCodeVerifier();
    } catch (error) {
      console.warn('⚠️ PKCE fallback: Error generating code verifier:', error);
      // Basic fallback using timestamp and random
      const timestamp = Date.now().toString();
      const random = Math.random().toString(36).substring(2);
      const fallbackVerifier = btoa(timestamp + random)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
      return fallbackVerifier;
    }
  }

  /**
   * Generate code challenge from code verifier using SHA-256 with fallbacks
   * @param {string} codeVerifier - The code verifier
   * @returns {Promise<string>} Base64URL-encoded code challenge
   */
  static async generateCodeChallenge(codeVerifier: string): Promise<string> {
    try {
      return await generateCodeChallenge(codeVerifier);
    } catch (error) {
      console.warn('⚠️ PKCE fallback: Error generating code challenge:', error);
      // If our utility fails, return the verifier as challenge (less secure but functional)
      console.warn('⚠️ Using code verifier as challenge - reduced security');
      return codeVerifier;
    }
  }

  /**
   * Generate PKCE parameters and store code verifier
   * @returns {Promise<{codeVerifier: string, codeChallenge: string}>}
   */
  static async generatePKCE(): Promise<{codeVerifier: string, codeChallenge: string}> {
    const codeVerifier = this.generateCodeVerifier();
    
    try {
      sessionStorage.setItem(CONFIG.STORAGE_KEYS.CODE_VERIFIER, codeVerifier);
    } catch (error) {
      console.warn('⚠️ Cannot store code verifier in sessionStorage:', error);
      // Store in memory as fallback (will be lost on page refresh)
      (window as any)._fallbackCodeVerifier = codeVerifier;
    }

    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    return {
      codeVerifier,
      codeChallenge
    };
  }

  /**
   * Retrieve stored code verifier with fallback
   * @returns {string|null} The stored code verifier
   */
  static getStoredCodeVerifier(): string | null {
    try {
      return sessionStorage.getItem(CONFIG.STORAGE_KEYS.CODE_VERIFIER) || (window as any)._fallbackCodeVerifier || null;
    } catch (error) {
      console.warn('⚠️ Cannot access sessionStorage, using memory fallback:', error);
      return (window as any)._fallbackCodeVerifier || null;
    }
  }

  /**
   * Clear stored code verifier
   */
  static clearStoredCodeVerifier(): void {
    try {
      sessionStorage.removeItem(CONFIG.STORAGE_KEYS.CODE_VERIFIER);
    } catch (error) {
      console.warn('⚠️ Cannot access sessionStorage:', error);
    }
    delete (window as any)._fallbackCodeVerifier;
  }
}

/**
 * Token management utilities
 */
class TokenManager {
  /**
   * Check if token is expired or will expire soon
   * @param {string} token - JWT access token
   * @returns {boolean} True if token is expired or expiring soon
   */
  static isTokenExpiredOrExpiringSoon(token: string): boolean {
    if (!token) {
      return true;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expTime = payload.exp * 1000;
      const currentTime = Date.now();
      const timeLeft = expTime - currentTime;

      return timeLeft <= 0 || timeLeft < CONFIG.TOKEN_REFRESH_BUFFER;
    } catch (error) {
      console.error('Error parsing token:', error);
      return true;
    }
  }

  /**
   * Get current access token from store
   * @returns {string|null} Access token or null
   */
  static getCurrentToken(): string | null {
    const authStore = useAuthStore();
    return authStore.getAccessToken || null;
  }

  /**
   * Get current refresh token from store
   * @returns {string|null} Refresh token or null
   */
  static getCurrentRefreshToken(): string | null {
    const authStore = useAuthStore();
    return authStore.getRefreshToken || null;
  }
}

/**
 * HTTP client factory with authentication
 */
class HTTPClientFactory {
  /**
   * Create axios instance with authentication headers
   * @returns {import('axios').AxiosInstance} Configured axios instance
   */
  static async createAuthenticatedClient(): Promise<import('axios').AxiosInstance> {
    const token = TokenManager.getCurrentToken();
    
    // Use relative URLs - all API requests will be made against the current origin
    const baseURL = '/';

    return axios.create({
      baseURL,
      headers: token ? {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      } : {
        'Content-Type': 'application/json'
      }
    });
  }
}

/**
 * Enhanced Authentication Service with comprehensive error handling
 */
class ArchiWebAuthService {
  private _configLoaded = false;
  private refreshPromise: Promise<TokenResponse> | null = null;

  constructor() {
    this._configLoaded = false;
    this._setupAxiosInterceptors();
  }

  /**
   * Setup global axios interceptors for request/response handling
   * @private
   */
  private _setupAxiosInterceptors(): void {
    // Request interceptor to inject authorization headers
    axios.interceptors.request.use(
      (config) => {
        // Skip auth for OAuth endpoints or if explicitly disabled
        if ((config as any).skipAuthInterceptor || this._isAuthEndpoint(config.url)) {
          return config;
        }

        const token = TokenManager.getCurrentToken();
        if (token) {
          config.headers = config.headers || {};
          (config.headers as any).Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    axios.interceptors.response.use(
      (response) => response,
      (error) => Promise.reject(error)
    );
  }

  /**
   * Check if URL is an authentication endpoint
   * @param {string} url - URL to check
   * @returns {boolean} True if it's an auth endpoint
   * @private
   */
  private _isAuthEndpoint(url?: string): boolean {
    return !!url && (
      url.includes('/oauth2/token') ||
      url.includes('/oauth2/authorize') ||
      url.includes('/oauth2/revoke')
    );
  }

  /**
   * Initialize the authentication service with configuration
   */
  async initialize(): Promise<void> {
    try {
      const config = await configService.getConfig();
      
      // Validate that all required config values are present
      if (!config.OAUTH?.clientId || !config.OAUTH?.scope || !config.OAUTH?.redirectUri) {
                throw new Error('Missing required configuration values in config.json. Required: OAUTH.clientId, OAUTH.scope, OAUTH.redirectUri');
      }
      
      CONFIG.CLIENT_ID = config.OAUTH.clientId;
      CONFIG.SCOPES = config.OAUTH.scope.join(' ');
      CONFIG.REDIRECT_URI = config.OAUTH.redirectUri;
      
      this._configLoaded = true;
      
      console.log('✅ Authentication service configuration loaded successfully:', {
        CLIENT_ID: CONFIG.CLIENT_ID,
        SCOPES: CONFIG.SCOPES,
        REDIRECT_URI: CONFIG.REDIRECT_URI,
        AUTH_SERVER: CONFIG.AUTH_SERVER
      });
    } catch (error) {
      console.error('❌ Critical error: Failed to load authentication configuration from config.json');
      
      // Show user-friendly notification for configuration errors
      showApiErrorNotification({
        message: 'Authentication configuration could not be loaded. Please contact support.',
        type: 'CONFIG_ERROR'
      } as any);
      
      throw new Error(`Authentication service configuration failed: ${(error as Error).message}`);
    }
  }

  /**
   * Vérifie si la configuration est complètement chargée
   * @returns {boolean} True if configuration is loaded and complete
   */
  isConfigLoaded(): boolean {
    return this._configLoaded && 
           !!CONFIG.CLIENT_ID && 
           !!CONFIG.SCOPES && 
           !!CONFIG.REDIRECT_URI;
  }

  /**
   * Obtient l'état actuel de la configuration
   * @returns {Object} Configuration status and values
   */
  getConfigStatus(): {
    loaded: boolean;
    complete: boolean;
    values: {
      CLIENT_ID: string | null;
      SCOPES: string | null;
      REDIRECT_URI: string | null;
      AUTH_SERVER: string;
    };
  } {
    return {
      loaded: this._configLoaded,
      complete: this.isConfigLoaded(),
      values: {
        CLIENT_ID: CONFIG.CLIENT_ID,
        SCOPES: CONFIG.SCOPES,
        REDIRECT_URI: CONFIG.REDIRECT_URI,
        AUTH_SERVER: CONFIG.AUTH_SERVER
      }
    };
  }

  /**
   * Check if the service is properly initialized
   */
  /**
   * S'assure que la configuration est chargée avant d'exécuter une opération
   * @private
   */
  private _ensureConfigLoaded(): void {
    if (!this._configLoaded) {
      throw new Error('Authentication service not initialized. Call initialize() first.');
    }
    
    // Double-check that all required config values are present
    if (!CONFIG.CLIENT_ID || !CONFIG.SCOPES || !CONFIG.REDIRECT_URI) {
      throw new Error('Authentication service configuration is incomplete. Ensure config.json contains all required values.');
    }
  }

  /**
   * Generate OAuth2 authorization URL with PKCE
   */
  async getAuthorizationUrl(state?: string): Promise<string> {
    this._ensureConfigLoaded();
    
    const { codeChallenge } = await PKCEUtils.generatePKCE();

    // Generate state parameter for CSRF protection
    const authState = state || this.generateState();
    sessionStorage.setItem(CONFIG.STORAGE_KEYS.AUTH_STATE, authState);
    
    // Construct redirect URI using current origin and configured path
    const redirectUri = `${window.location.origin}${CONFIG.REDIRECT_URI}`;
    const currentLocale = sessionStorage.getItem(CONFIG.STORAGE_KEYS.LOCALE) || 'en';

    // Build authorization URL parameters
    const params = new URLSearchParams({
      client_id: CONFIG.CLIENT_ID!,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: CONFIG.SCOPES!,
      state: authState,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      lang: currentLocale
    });

    const endpoints = getEndpoints();
    return `${endpoints.AUTHORIZE}?${params.toString()}`;
  }

  /**
   * Handle OAuth2 callback and exchange code for tokens
   */
  async handleCallback(code: string, state: string): Promise<AuthResponse> {
    this._ensureConfigLoaded();
    
    try {
      // Validate state parameter
      const storedState = sessionStorage.getItem(CONFIG.STORAGE_KEYS.AUTH_STATE);
      if (state !== storedState) {
        throw new Error('Invalid state parameter - possible CSRF attack');
      }

      // Get stored PKCE code verifier
      const codeVerifier = PKCEUtils.getStoredCodeVerifier();
      if (!codeVerifier) {
        throw new Error('Missing PKCE code verifier');
      }

      // Exchange authorization code for tokens
      const tokenResponse = await this.exchangeCodeForTokens(code, codeVerifier);
      console.log('🔑 OAuth tokens received:', {
        hasAccessToken: !!tokenResponse.access_token,
        hasRefreshToken: !!tokenResponse.refresh_token,
        tokenType: tokenResponse.token_type,
        expiresIn: tokenResponse.expires_in
      });
      
      // Store tokens
      this.storeTokens(tokenResponse);
      
      // Fetch user profile
      const user = await this.fetchUserProfile(tokenResponse.access_token);
      
      // Update store
      const authStore = useAuthStore()
      authStore.setAccessToken(tokenResponse.access_token);
      authStore.setUser(user);
      // isAuthenticated will be automatically set to true by setUser

      // Clean up stored values
      PKCEUtils.clearStoredCodeVerifier();
      sessionStorage.removeItem(CONFIG.STORAGE_KEYS.AUTH_STATE);

      return {
        success: true,
        user,
        accessToken: tokenResponse.access_token,
        refreshToken: tokenResponse.refresh_token
      };

    } catch (error) {
      console.error('OAuth callback error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Authentication failed'
      };
    }
  }

  /**
   * Initiate OAuth2 login flow
   */
  async login(): Promise<void> {
    try {
      const authUrl = await this.getAuthorizationUrl();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error initiating login:', error);
      throw new Error('Failed to initiate login process');
    }
  }

  /**
   * Direct login with username/password
   */
  async loginWithCredentials(username: string, password: string): Promise<AuthResponse> {
    this._ensureConfigLoaded();
    
    try {
      const credentials: LoginCredentials = { username, password };
      
      const response: AxiosResponse<TokenResponse> = await axios.post(
        getEndpoints().TOKEN,
        credentials,
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );

      const tokenData = response.data;
      console.log('🔑 Direct login tokens received:', {
        hasAccessToken: !!tokenData.access_token,
        hasRefreshToken: !!tokenData.refresh_token,
        tokenType: tokenData.token_type,
        expiresIn: tokenData.expires_in
      });
      
      // Store tokens
      this.storeTokens(tokenData);
      
      // Fetch user profile
      const user = await this.fetchUserProfile(tokenData.access_token);
      
      return {
        success: true,
        user,
        accessToken: tokenData.access_token,
        refreshToken: tokenData.refresh_token
      };

    } catch (error) {
      console.error('Login error:', error);
      let message = 'Login failed';
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          message = 'Invalid username or password';
        } else if (error.response?.status === 403) {
          message = 'Account is disabled or locked';
        } else if (error.response?.data?.message) {
          message = error.response.data.message;
        }
      }
      
      return {
        success: false,
        message
      };
    }
  }

  /**
   * Logout user by calling server logout endpoint and clearing tokens
   */
  async logout(redirectToLogin: boolean = true): Promise<void> {
    console.log('🚪 Starting logout process...');

    try {
      // Get current token from store
      const token = this.getStoredAccessToken();
      
      if (token) {
        console.log('📤 Calling server logout endpoint...');
        
        // Call the OAuth2 token revocation endpoint
        const endpoints = getEndpoints();
        
        await axios.post(endpoints.LOGOUT, new URLSearchParams({
          token: token,
          token_type_hint: 'access_token'
        }), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          withCredentials: true, // Include JSESSIONID cookie
          skipAuthInterceptor: true
        } as any);
        
        console.log('✅ Server logout completed');
      } else {
        console.log('ℹ️ No active token found, skipping server logout');
      }
    } catch (error) {
      // Don't fail the logout process if server logout fails
      console.warn('⚠️ Server logout failed, continuing with local cleanup:', (error as Error).message);
      
      // Only show notification for severe server errors, not network issues
      if (axios.isAxiosError(error) && error.response?.status && error.response.status >= 500) {
        showApiWarningNotification({
          message: 'Logout completed locally, but server cleanup may have failed.',
          type: 'SERVICE_UNAVAILABLE'
        } as any);
      }
    }    console.log('🧹 Clearing local authentication data...');

    // Clear tokens from store
    useAuthStore().setAccessToken(null);
    useAuthStore().setRefreshToken(null);

    // Clear local storage
    PKCEUtils.clearStoredCodeVerifier();
    sessionStorage.removeItem(CONFIG.STORAGE_KEYS.AUTH_STATE);

    console.log('✅ Local cleanup complete');

    // Redirect to login if requested
    if (redirectToLogin) {
      console.log('🔄 Redirecting to login...');
      window.location.href = "/";
    }
  }

  /**
   * Logout user and redirect to a specific URL
   * @param {string} redirectUrl - URL to redirect to after logout
   * @returns {Promise<void>}
   */
  async logoutAndRedirect(redirectUrl: string): Promise<void> {
    await this.logout(false); // Don't redirect to login
    window.location.href = redirectUrl;
  }

  /**
   * Silent logout - clear local data without server call or redirect
   * Useful for handling token expiration or session cleanup
   * @returns {void}
   */
  silentLogout(): void {
    console.log('🔇 Performing silent logout...');

    // Clear tokens from store
    useAuthStore().setAccessToken(null);
    useAuthStore().setRefreshToken(null);

    // Clear local storage
    PKCEUtils.clearStoredCodeVerifier();
    sessionStorage.removeItem(CONFIG.STORAGE_KEYS.AUTH_STATE);

    console.log('✅ Silent logout complete');
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(refreshToken?: string): Promise<TokenResponse> {
    console.log('🔄 Auth service: Starting token refresh...');
    this._ensureConfigLoaded();
    
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      console.log('🔄 Auth service: Refresh already in progress, waiting...');
      return this.refreshPromise;
    }

    const tokenToUse = refreshToken || this.getStoredRefreshToken();
    console.log('🔄 Auth service: Refresh token available:', !!tokenToUse);
    
    if (!tokenToUse) {
      throw new Error('No refresh token available');
    }

    this.refreshPromise = this.performTokenRefresh(tokenToUse);
    
    try {
      const result = await this.refreshPromise;
      console.log('✅ Auth service: Token refresh successful');
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Validate if access token is still valid
   */
  async validateToken(token: string): Promise<boolean> {
    this._ensureConfigLoaded();
    
    try {
      const response = await axios.post(
        getEndpoints().TOKEN,
        { token },
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      return response.data.valid === true;
    } catch (error) {
      console.error('Token validation failed:', error);
      return false;
    }
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User | null> {
    this._ensureConfigLoaded();
    
    const accessToken = this.getStoredAccessToken();
    
    if (!accessToken) {
      return null;
    }

    try {
      return await this.fetchUserProfile(accessToken);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      return null;
    }
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    const token = TokenManager.getCurrentToken();
    return !!token && !TokenManager.isTokenExpiredOrExpiringSoon(token);
  }

  /**
   * Refresh token if needed (expired or expiring soon)
   * @returns {Promise<boolean>} True if token is valid or refresh was successful
   */
  async refreshTokenIfNeeded(): Promise<boolean> {
    const token = TokenManager.getCurrentToken();
    if (!token) {
      return false;
    }

    if (TokenManager.isTokenExpiredOrExpiringSoon(token)) {
      return !!(await this.refreshToken());
    }

    return true; // Token is still valid
  }

  /**
   * Get current user profile
   * @returns {Promise<Object>} User profile data
   */
  async getUserProfile(): Promise<User | null> {
    try {
      const http = await HTTPClientFactory.createAuthenticatedClient();
      const endpoints = getEndpoints();
      const response = await http.get(endpoints.USER_PROFILE);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        // Try to refresh token and retry
        const refreshed = await this.refreshTokenIfNeeded();
        if (refreshed) {
          const http = await HTTPClientFactory.createAuthenticatedClient();
          const endpoints = getEndpoints();
          const response = await http.get(endpoints.USER_PROFILE);
          return response.data;
        }
      }
      throw error;
    }
  }

  /**
   * Create an authenticated HTTP client for API calls
   * @returns {Promise<import('axios').AxiosInstance>} Configured axios instance
   */
  async createHttpClient(): Promise<import('axios').AxiosInstance> {
    return await HTTPClientFactory.createAuthenticatedClient();
  }

  /**
   * Get stored access token from store
   */
  getStoredAccessToken(): string | null {
    const authStore = useAuthStore();
    const token = authStore.getAccessToken;
    console.log('🔍 Getting stored access token:', {
      tokenExists: !!token,
      tokenValue: token ? 'present' : 'null'
    });
    return token || null;
  }

  /**
   * Get stored refresh token from store
   */
  private getStoredRefreshToken(): string | null {
    const authStore = useAuthStore();
    const token = authStore.getRefreshToken;
    console.log('🔍 Getting stored refresh token:', {
      exists: !!token
    });
    return token || null;
  }

  /**
   * Exchange authorization code for tokens using PKCE
   */
  private async exchangeCodeForTokens(code: string, codeVerifier: string): Promise<TokenResponse> {
    const redirectUri = `${window.location.origin}${CONFIG.REDIRECT_URI}`;
    const response: AxiosResponse<TokenResponse> = await axios.post(
      getEndpoints().TOKEN,
      new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
        redirect_uri: redirectUri,
        client_id: CONFIG.CLIENT_ID!,
        scope: CONFIG.SCOPES!,
        code_verifier: codeVerifier,
      }),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      }
    );

    return response.data;
  }

  /**
   * Perform actual token refresh
   */
  private async performTokenRefresh(refreshToken: string): Promise<TokenResponse> {
    console.log('🔄 Auth service: Making token refresh HTTP request...');
    
    const response: AxiosResponse<TokenResponse> = await axios.post(
      getEndpoints().TOKEN,
      new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: CONFIG.CLIENT_ID!,
        scope: CONFIG.SCOPES!,
      }),
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      }
    );

    console.log('🔄 Auth service: Token refresh response received');
    const tokenData = response.data;
    
    // Store new tokens
    this.storeTokens(tokenData);
    
    // Update store
    useAuthStore().setAccessToken(tokenData.access_token);
    console.log('✅ Auth service: Tokens stored and store updated');

    return tokenData;
  }

  /**
   * Fetch user profile from userinfo endpoint
   */
  private async fetchUserProfile(accessToken: string): Promise<User> {
    const response: AxiosResponse<User> = await axios.get(
      getEndpoints().USER_PROFILE,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );

    return response.data;
  }

  /**
   * Store tokens in Vuex store
   */
  private storeTokens(tokens: TokenResponse): void {
    console.log('💾 Storing tokens:', {
      hasAccessToken: !!tokens.access_token,
      hasRefreshToken: !!tokens.refresh_token
    });
    
    useAuthStore().setAccessToken(tokens.access_token);
    
    if (tokens.refresh_token) {
      useAuthStore().setRefreshToken(tokens.refresh_token);
      console.log('✅ Refresh token stored in Vuex store');
    } else {
      console.log('⚠️ No refresh token to store');
    }
  }

  /**
   * Clear all stored tokens from Vuex store
   */
  private clearStoredTokens(): void {
    useAuthStore().setAccessToken(null);
    useAuthStore().setRefreshToken(null);
  }

  /**
   * Generate cryptographically secure state parameter
   */
  private generateState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Check if token is expired (with buffer)
   */
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      
      return currentTime >= (expirationTime - CONFIG.TOKEN_REFRESH_BUFFER);
    } catch (error) {
      console.error('Error parsing token:', error);
      return true; // Assume expired if we can't parse
    }
  }
}

// Export singleton instance
const archiWebAuthService = new ArchiWebAuthService();
export default archiWebAuthService;
